import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Typography,
    Box,
    Alert,
    CircularProgress
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';

interface SessionLoginModalProps {
    open: boolean;
    onLogin: (email: string, pass: string) => Promise<void>;
}

const SessionLoginModal: React.FC<SessionLoginModalProps> = ({ open, onLogin }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            await onLogin(email, password);
        } catch (err: any) {
            console.error('Session login error', err);
            setError(err.response?.data?.message || 'Identifiants invalides');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    p: 1
                }
            }}
        >
            <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1
                }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 50,
                        height: 50,
                        borderRadius: '50%',
                        bgcolor: 'primary.light',
                        color: 'primary.main',
                        mb: 1
                    }}>
                        <LockIcon />
                    </Box>
                    <Typography variant="h5" fontWeight="700">Session expirée</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Veuillez vous reconnecter pour continuer
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent>
                <form id="session-login-form" onSubmit={handleSubmit}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        {error && <Alert severity="error">{error}</Alert>}

                        <TextField
                            label="Email"
                            type="email"
                            fullWidth
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoFocus
                        />
                        <TextField
                            label="Mot de passe"
                            type="password"
                            fullWidth
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                    </Box>
                </form>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 4 }}>
                <Button
                    form="session-login-form"
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading}
                    sx={{
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: '1rem',
                        fontWeight: 600,
                        boxShadow: 'none',
                        '&:hover': {
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }
                    }}
                >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Se connecter'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SessionLoginModal;
